import { RuleId } from '@wix/a11y-rules';
import { useExperiments } from '@wix/fe-essentials/react';
import { groupBy } from 'lodash';
import { useMemo } from 'react';
import { usePanelStateAtom } from '../../../../../hooks/rules-state';
import { useColorContrastRule, useHeadingStructureRule, useInaccessibleComponentsRule, usePageAutoDomOrderRule, useValidStaticPageTitleRule } from '../../../../../rule-mappers/page-level-rules';
import { useAltTextRule } from '../../../../../rule-mappers/page-level-rules/use-alt-text-rule';
import { useMediaAlternativesRule } from '../../../../../rule-mappers/page-level-rules/use-media-alternatives-rule';
export const useStaticPageRuleResults = pageId => {
  const {
    experiments
  } = useExperiments();
  const {
    getGroupData
  } = usePanelStateAtom();
  const groupData = getGroupData(pageId);
  const {
    getColorContrast
  } = useColorContrastRule();
  const {
    getHeadingStructure
  } = useHeadingStructureRule();
  const {
    getValidStaticPageTitleProps
  } = useValidStaticPageTitleRule();
  const {
    getAltTextRule
  } = useAltTextRule();
  const {
    getMediaAlternatives
  } = useMediaAlternativesRule();
  const {
    getInaccessibleComponents
  } = useInaccessibleComponentsRule();
  const {
    getPageAutoDomOrderRule
  } = usePageAutoDomOrderRule();
  const ruleResults = useMemo(() => {
    if (!(groupData != null && groupData.ruleExecutionResults)) {
      return [];
    }
    const resultsGroupedByRule = groupBy(groupData.ruleExecutionResults, 'ruleId');
    const ruleMappers = {
      [RuleId.VALID_STATIC_PAGE_TITLE]: () => {
        var _resultsGroupedByRule;
        const result = (_resultsGroupedByRule = resultsGroupedByRule[RuleId.VALID_STATIC_PAGE_TITLE]) == null ? void 0 : _resultsGroupedByRule[0];
        return result ? getValidStaticPageTitleProps({
          pageId,
          ruleExecutionResult: result
        }) : null;
      },
      [RuleId.INACCESSIBLE_COMPONENTS]: () => {
        const result = resultsGroupedByRule[RuleId.INACCESSIBLE_COMPONENTS];
        return result ? getInaccessibleComponents({
          pageId,
          ruleExecutionResult: result
        }) : null;
      },
      [RuleId.HEADING_STRUCTURE]: () => {
        var _resultsGroupedByRule2;
        const result = (_resultsGroupedByRule2 = resultsGroupedByRule[RuleId.HEADING_STRUCTURE]) == null ? void 0 : _resultsGroupedByRule2[0];
        return result ? getHeadingStructure({
          pageId,
          ruleExecutionResult: result
        }) : null;
      },
      [RuleId.STATIC_ALT_TEXT]: () => {
        const result = resultsGroupedByRule[RuleId.STATIC_ALT_TEXT];
        return getAltTextRule({
          pageId,
          ruleExecutionResult: result ?? []
        });
      },
      [RuleId.COLOR_CONTRAST]: () => {
        var _resultsGroupedByRule3;
        const result = (_resultsGroupedByRule3 = resultsGroupedByRule[RuleId.COLOR_CONTRAST]) == null ? void 0 : _resultsGroupedByRule3[0];
        return result ? getColorContrast({
          pageId,
          ruleExecutionResult: result
        }) : null;
      },
      [RuleId.MEDIA_ALTERNATIVES]: () => {
        const result = resultsGroupedByRule[RuleId.MEDIA_ALTERNATIVES];
        return result ? getMediaAlternatives({
          pageId,
          ruleExecutionResult: result
        }) : null;
      },
      [RuleId.PAGE_AUTO_DOM_ORDER]: () => {
        var _resultsGroupedByRule4;
        const result = (_resultsGroupedByRule4 = resultsGroupedByRule[RuleId.PAGE_AUTO_DOM_ORDER]) == null ? void 0 : _resultsGroupedByRule4[0];
        return result ? getPageAutoDomOrderRule({
          pageId,
          ruleExecutionResult: result
        }) : null;
      }
    };
    return Object.values(ruleMappers).map(mapper => mapper()).filter(result => result !== null && (!result.enabledByExperiment || experiments.enabled(result.enabledByExperiment)));
  }, [groupData == null ? void 0 : groupData.ruleExecutionResults, pageId, experiments, getAltTextRule, getColorContrast, getHeadingStructure, getInaccessibleComponents, getMediaAlternatives, getPageAutoDomOrderRule, getValidStaticPageTitleProps]);
  return ruleResults;
};
//# sourceMappingURL=use-static-page-rule-results.js.map